import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateDimmed,
  updateLoginVisible,
} from "../../../../features/forms/formsSlice";
import {
  LoginFormWrapper,
  InputWrapper,
  Username,
  Password,
  Caption,
  LostPassword,
  EmailSent,
  ButtonsWrapper,
  LogIn,
  Cancel,
  ResetLinkWrapper,
  SendResetLink,
  EmailResetLink,
  WrongLogin,
} from "./LoginFormStyles";
import { loginUser, clearAuthError, clearJustLoggedIn } from "../../../../features/auth/authSlice";
import { fetchStudentLessons } from "../../../../features/studentLessons/studentLessonsSlice";
import { API_URL } from "../../../../components/01_config/api";

// const API_URL = "http://localhost:4000";

const LoginForm = ({ loginVisible }) => {
  const [usernameValue, setUsernameValue] = useState("");
  const [passwordValue, setPasswordValue] = useState("");
  const [emailValue, setEmailValue] = useState("");

  const [showResetLink, setShowResetLink] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Local error for validation + reset errors
  const [localError, setLocalError] = useState("");

  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  // Show WrongLogin if either localError OR backend error exists
  const showWrongLogin = !!(localError || error);

  const clickShowResetLink = (e) => {
    e.preventDefault();
    setShowResetLink(true);
    setEmailSent(false);
    setLocalError("");
  };

  const clickSendResetLink = async (e) => {
    e.preventDefault();

    if (!emailValue) {
      setLocalError("Please enter your email address to get a reset link.");
      setEmailSent(false);
      return;
    }

    try {
      setLocalError("");
      setEmailSent(false);

      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailValue }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          data.message ||
          "Unable to send reset link. Please check the email and try again.";
        setLocalError(msg);
        return;
      }

      // success
      setEmailSent(true);
    } catch {
      setLocalError("Network error. Please try again.");
      setEmailSent(false);
    }
  };

  const clickLogin = (e) => {
    e.preventDefault();

    const email = usernameValue; // treating username field as email

    // Simple front-end validation
    if (!email) {
      setLocalError("Please enter your email address.");
      return;
    }
    if (!passwordValue) {
      setLocalError("Please enter your password.");
      return;
    }

    setLocalError("");
dispatch(loginUser({ email, password: passwordValue }))
  .unwrap()
  .then(() => {
    // close modal & clear fields immediately
    setShowResetLink(false);
    setEmailSent(false);
    setPasswordValue("");
    setEmailValue("");
    setUsernameValue("");
    dispatch(updateLoginVisible(false));
    dispatch(updateDimmed(false));

    // start lessons fetch in background (no await)
    dispatch(fetchStudentLessons())
      .unwrap()
      .finally(() => {
        dispatch(clearJustLoggedIn());
      });
  })
  .catch((error) => {
    setLocalError(typeof error === "string" ? error : (error?.message || "Login failed"));
  })
}

  const clickCancel = (e) => {
    e.preventDefault();
    setShowResetLink(false);
    setEmailSent(false);
    setEmailValue("");
    setUsernameValue("");
    setPasswordValue("");
    setLocalError("");
    dispatch(clearAuthError());
    dispatch(updateLoginVisible(false));
    dispatch(updateDimmed(false));
  };

  // When the form is hidden, clear only *local* errors and fields.
  // Do NOT clear backend error here, so wrong-password messages can show.
  useEffect(() => {
    if (!loginVisible) {
      setLocalError("");
      setShowResetLink(false);
      setEmailSent(false);
      setEmailValue("");
      setUsernameValue("");
      setPasswordValue("");
      // important: do NOT dispatch(clearAuthError()) here
    }
  }, [loginVisible]);

  // Clear local errors as user types (so messages disappear once they start fixing input)
  const handleUsernameChange = (e) => {
    setUsernameValue(e.target.value);
    if (localError) setLocalError("");
  };

  const handlePasswordChange = (e) => {
    setPasswordValue(e.target.value);
    if (localError) setLocalError("");
  };

  const handleEmailChange = (e) => {
    setEmailValue(e.target.value);
    if (localError) setLocalError("");
  };

  return (
    <LoginFormWrapper loginVisible={loginVisible}>
      <InputWrapper>
        <Caption>Username (email):</Caption>
        <Username
          type="email"
          value={usernameValue}
          onChange={handleUsernameChange}
        />
      </InputWrapper>
      <InputWrapper>
        <Caption>Password:</Caption>
        <Password
          type="password"
          value={passwordValue}
          onChange={handlePasswordChange}
        />
        <LostPassword onClick={clickShowResetLink}>
          Lost password?
          <EmailSent emailSent={emailSent}>
            Link sent — it may take up to 5 minutes to arrive.
            Please also check your spam/junk folder.
          </EmailSent>
        </LostPassword>
        <ResetLinkWrapper showResetLink={showResetLink}>
          <EmailResetLink
            type="email"
            placeholder="Enter your email"
            value={emailValue}
            onChange={handleEmailChange}
          />
          <SendResetLink onClick={clickSendResetLink}>
            Send Reset Link
          </SendResetLink>
        </ResetLinkWrapper>
      </InputWrapper>

      <WrongLogin showWrongLogin={showWrongLogin}>
        {localError ||
          error ||
          "Please check your login details and try again."}
      </WrongLogin>

      <ButtonsWrapper>
        <LogIn onClick={clickLogin} disabled={loading}>
          {loading ? "Logging in..." : "Log in"}
        </LogIn>
        <Cancel onClick={clickCancel}>Cancel</Cancel>
      </ButtonsWrapper>
    </LoginFormWrapper>
  );
};

export default LoginForm;
